/**
 * Encryption Utilities for Client-Side
 * Uses AES-256-GCM for authenticated encryption
 * Must match server-side encryption in functions/common/securityUtils.js
 */

import * as Crypto from 'expo-crypto';
import CryptoJS from 'crypto-js';

// IMPORTANT: This must match REQUEST_SIGNATURE_SECRET on the server
// The server uses process.env.REQUEST_SIGNATURE_SECRET
const ENCRYPTION_SECRET = process.env.EXPO_PUBLIC_ENCRYPTION_SECRET || 'default-secret-change-me';

/**
 * Convert string to Uint8Array
 */
function stringToUint8Array(str) {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

/**
 * Convert Uint8Array to base64
 */
function uint8ArrayToBase64(array) {
  let binary = '';
  const len = array.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(array[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 to Uint8Array
 */
function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Generate SHA-256 hash of the encryption secret to create a 256-bit key
 */
async function deriveKey() {
  const secretBytes = stringToUint8Array(ENCRYPTION_SECRET);
  const hashBuffer = await Crypto.digest(Crypto.CryptoDigestAlgorithm.SHA256, secretBytes);
  return new Uint8Array(hashBuffer);
}

/**
 * Encrypt data for sending to server
 * Uses AES-256-GCM for authenticated encryption
 * Returns format: { encrypted: true, payload: "iv.authTag.encryptedData" }
 */
export async function encryptData(data) {
  try {
    // For web environment, use Web Crypto API
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
      return await encryptDataWeb(data);
    }
    
    // For React Native, use CryptoJS which provides AES-GCM compatible encryption
    const jsonData = JSON.stringify(data);
    
    // Generate random IV (16 bytes for CTR mode)
    const ivArray = await Crypto.getRandomBytesAsync(16);
    
    // Convert Uint8Array to CryptoJS WordArray properly
    const ivWords = [];
    for (let i = 0; i < ivArray.length; i += 4) {
      const word = (ivArray[i] << 24) | 
                   (ivArray[i + 1] << 16) | 
                   (ivArray[i + 2] << 8) | 
                   (ivArray[i + 3] || 0);
      ivWords.push(word);
    }
    const iv = CryptoJS.lib.WordArray.create(ivWords, ivArray.length);
    
    // Derive key using SHA-256
    const key = CryptoJS.SHA256(ENCRYPTION_SECRET);
    
    // Encrypt using AES-256-CTR (CryptoJS doesn't have GCM, but CTR is secure)
    // We'll use CTR mode with HMAC for authentication to simulate GCM
    const encrypted = CryptoJS.AES.encrypt(jsonData, key, {
      iv: iv,
      mode: CryptoJS.mode.CTR,
      padding: CryptoJS.pad.NoPadding
    });
    
    // Extract ciphertext
    const ciphertext = encrypted.ciphertext;
    
    // Create HMAC authentication tag (simulates GCM's auth tag)
    // CRITICAL: Must use key as hex string for HMAC, not WordArray
    const keyHex = key.toString(CryptoJS.enc.Hex);
    const authData = iv.clone().concat(ciphertext);
    const authDataHex = authData.toString(CryptoJS.enc.Hex);
    
    // Compute HMAC using hex strings to match Node.js behavior
    const authTagFull = CryptoJS.HmacSHA256(CryptoJS.enc.Hex.parse(authDataHex), CryptoJS.enc.Hex.parse(keyHex));
    
    // Take first 16 bytes (128 bits) of HMAC for auth tag
    const authTagWords = [];
    for (let i = 0; i < 4; i++) {
      authTagWords.push(authTagFull.words[i]);
    }
    const authTag = CryptoJS.lib.WordArray.create(authTagWords);
    
    // Convert to base64
    const ivBase64 = iv.toString(CryptoJS.enc.Base64);
    const authTagBase64 = authTag.toString(CryptoJS.enc.Base64);
    const encryptedBase64 = ciphertext.toString(CryptoJS.enc.Base64);
    
    return {
      encrypted: true,
      payload: `${ivBase64}.${authTagBase64}.${encryptedBase64}`
    };
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Encrypt data using Web Crypto API (for web environment)
 */
async function encryptDataWeb(data) {
  try {
    const jsonData = JSON.stringify(data);
    
    // Derive key from secret
    const secretBytes = new TextEncoder().encode(ENCRYPTION_SECRET);
    const keyMaterial = await window.crypto.subtle.digest('SHA-256', secretBytes);
    const key = await window.crypto.subtle.importKey(
      'raw',
      keyMaterial,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );
    
    // Generate IV
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt
    const dataBytes = new TextEncoder().encode(jsonData);
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      dataBytes
    );
    
    // Extract encrypted data and auth tag
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const encryptedData = encryptedArray.slice(0, -16);
    const authTag = encryptedArray.slice(-16);
    
    // Convert to base64
    const ivBase64 = btoa(String.fromCharCode(...iv));
    const authTagBase64 = btoa(String.fromCharCode(...authTag));
    const encryptedBase64 = btoa(String.fromCharCode(...encryptedData));
    
    return {
      encrypted: true,
      payload: `${ivBase64}.${authTagBase64}.${encryptedBase64}`
    };
  } catch (error) {
    console.error('Web encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Decrypt data received from server
 * Expects format: { encrypted: true, payload: "iv.authTag.encryptedData" }
 */
export async function decryptData(encryptedResponse) {
  try {
    // If not encrypted, return as-is
    if (!encryptedResponse.encrypted) {
      return encryptedResponse;
    }
    
    // For web environment, use Web Crypto API
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
      return await decryptDataWeb(encryptedResponse.payload);
    }
    
    const parts = encryptedResponse.payload.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid encrypted payload format');
    }
    
    // Parse base64 components
    const iv = CryptoJS.enc.Base64.parse(parts[0]);
    const authTag = CryptoJS.enc.Base64.parse(parts[1]);
    const ciphertext = CryptoJS.enc.Base64.parse(parts[2]);
    
    // Derive key using SHA-256
    const key = CryptoJS.SHA256(ENCRYPTION_SECRET);
    
    // Verify HMAC authentication tag
    // CRITICAL: Must use key as hex string for HMAC, not WordArray
    const keyHex = key.toString(CryptoJS.enc.Hex);
    const authData = iv.clone().concat(ciphertext);
    const authDataHex = authData.toString(CryptoJS.enc.Hex);
    
    // Compute HMAC using hex strings to match Node.js behavior
    const computedAuthTagFull = CryptoJS.HmacSHA256(CryptoJS.enc.Hex.parse(authDataHex), CryptoJS.enc.Hex.parse(keyHex));
    
    // Take first 16 bytes (128 bits) of HMAC for comparison
    const computedAuthTagWords = [];
    for (let i = 0; i < 4; i++) {
      computedAuthTagWords.push(computedAuthTagFull.words[i]);
    }
    const computedAuthTag = CryptoJS.lib.WordArray.create(computedAuthTagWords);
    
    // Compare auth tags
    if (authTag.toString() !== computedAuthTag.toString()) {
      throw new Error('Authentication tag verification failed');
    }
    
    // Decrypt using AES-256-CTR
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext },
      key,
      {
        iv: iv,
        mode: CryptoJS.mode.CTR,
        padding: CryptoJS.pad.NoPadding
      }
    );
    
    const jsonData = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!jsonData) {
      throw new Error('Decryption failed - invalid data');
    }
    
    return JSON.parse(jsonData);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt data');
  }
}

/**
 * Decrypt data using Web Crypto API (for web environment)
 */
async function decryptDataWeb(payload) {
  try {
    const parts = payload.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid encrypted payload format');
    }
    
    const iv = new Uint8Array(atob(parts[0]).split('').map(c => c.charCodeAt(0)));
    const authTag = new Uint8Array(atob(parts[1]).split('').map(c => c.charCodeAt(0)));
    const encryptedData = new Uint8Array(atob(parts[2]).split('').map(c => c.charCodeAt(0)));
    
    // Derive key from secret
    const secretBytes = new TextEncoder().encode(ENCRYPTION_SECRET);
    const keyMaterial = await window.crypto.subtle.digest('SHA-256', secretBytes);
    const key = await window.crypto.subtle.importKey(
      'raw',
      keyMaterial,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    
    // Combine encrypted data and auth tag
    const combined = new Uint8Array(encryptedData.length + authTag.length);
    combined.set(encryptedData);
    combined.set(authTag, encryptedData.length);
    
    // Decrypt
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      combined
    );
    
    const decoder = new TextDecoder();
    const jsonData = decoder.decode(decryptedBuffer);
    
    return JSON.parse(jsonData);
  } catch (error) {
    console.error('Web decryption error:', error);
    throw new Error('Failed to decrypt data');
  }
}

export default {
  encryptData,
  decryptData
};
